import { Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { TrovataAppState } from 'src/app/core/models/state.model';
import { GetPlaidItemsResponse, PlaidItem } from 'src/app/shared/models/widgets.model';
import { WidgetsService } from 'src/app/shared/services/widgets.service';
import { catchError, combineLatest, Observable, take, tap, throwError, lastValueFrom, filter, firstValueFrom } from 'rxjs';
import { ClearPlaidState, GetPlaidItems, InitPlaidState, ResetPlaidState } from '../actions/plaid.actions';
import { CustomerFeatureState } from './customer-feature.state';

export class PlaidStateModel {
	plaidItems: PlaidItem[];
}

@State<PlaidStateModel>({
	name: 'plaid',
	defaults: {
		plaidItems: null,
	},
})
@Injectable()
export class PlaidState {
	private restrictedFeatureIds$: Observable<number[]>;

	private appReady$: Observable<boolean>;

	constructor(
		private store: Store,
		private widgetsService: WidgetsService
	) {
		this.appReady$ = this.store.select((state: TrovataAppState) => state.core.appReady);
		this.restrictedFeatureIds$ = this.store.select(CustomerFeatureState.restrictedFeatureIds);
	}

	@Action(InitPlaidState)
	async initPlaidState(context: StateContext<PlaidStateModel>): Promise<void> {
		const [appReady, restrictedFeatureIds]: [boolean, number[]] = await firstValueFrom(
			combineLatest([this.appReady$, this.restrictedFeatureIds$]).pipe(
				filter(([ready, ids]: [boolean, number[]]) => !!ready && !!ids),
				take(1)
			)
		);

		await lastValueFrom(context.dispatch([new GetPlaidItems()]));
	}

	@Action(ClearPlaidState)
	clearPlaidState(context: StateContext<PlaidStateModel>) {
		const state: PlaidStateModel = context.getState();
		Object.keys(state).forEach((key: string) => {
			state[key] = null;
		});
		context.patchState(state);
	}

	@Action(ResetPlaidState)
	resetPlaidState(context: StateContext<PlaidStateModel>) {
		context.dispatch(new GetPlaidItems());
	}

	@Action(GetPlaidItems)
	getPlaidItems(context: StateContext<PlaidStateModel>) {
		return this.widgetsService.getPlaidItems().pipe(
			tap((getPlaidItemsResponse: GetPlaidItemsResponse) => {
				const state: PlaidStateModel = context.getState();
				state.plaidItems = this.sortPlaidItemsByName(getPlaidItemsResponse.items);
				context.patchState(state);
			}),
			catchError(error => throwError(() => error))
		);
	}

	private sortPlaidItemsByName(plaidItems: PlaidItem[]): PlaidItem[] {
		plaidItems = plaidItems.sort((plaidItemA: PlaidItem, plaidItemB: PlaidItem) => plaidItemA.institutionName.localeCompare(plaidItemB.institutionName));
		return plaidItems;
	}
}
